import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { apiCall } from '../lib/api';
import { cn, getTimeAgo } from '../lib/utils';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { TenantTree, SiteTreeNode } from '../components/TenantTree';
import { accountDisplayName } from '../lib/novaAccounts';
import { RepeatCadenceEditor, RepeatCadenceValue } from '../components/RepeatCadenceEditor';
import {
  ImageDisplayEditor,
  ImageDisplayValue,
  ResolvedImageDisplayConfig,
  deserializeImageDisplay,
  serializeImageDisplayForTenant,
} from '../components/ImageDisplayEditor';

interface Tenant {
  id: number;
  source_system: string;
  source_system_name: string;
  external_id: string;
  name: string;
  display_name?: string;
  metadata: any;
  nova_account_id: string | null;
  account_label: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  last_seen: string | null;
  partner_count: string;
  location_count?: string;
}

interface SourceSystem {
  id: string;
  name: string;
  enabled: boolean;
}

interface NovaAccount {
  id: string;
  source_system: string;
  username?: string | null;
  username_secret_ref?: string | null;
  site_ids: string | null;
  enabled: boolean;
}

interface ChildEntry {
  external_id: string;
  name: string;
  parent_external_id: string | null;
}

interface HierarchyPreviewNode extends ChildEntry {
  children: HierarchyPreviewNode[];
}

interface TenantFormData {
  source_system: string;
  external_id: string;
  name: string;
  metadata: string;
  nova_account_id: string;
  enabled: boolean;
}

const emptyForm: TenantFormData = {
  source_system: '',
  external_id: '',
  name: '',
  metadata: '',
  nova_account_id: '',
  enabled: true,
};

const emptyCadence: RepeatCadenceValue = { enabled: null, cadenceSeconds: {} };

interface InheritedCadence {
  enabled: boolean;
  cadenceSeconds: Record<string, number>;
  partnerName?: string;
}

const GENERATED_METADATA_KEYS = [
  'siteIds',
  'siteNames',
  'siteParents',
  'tenantName',
  'novaAccountId',
];

function formatEditableMetadata(metadata: any): string {
  if (!metadata) return '';
  if (typeof metadata !== 'object' || Array.isArray(metadata)) {
    return JSON.stringify(metadata);
  }

  const customMetadata = { ...metadata };
  for (const key of GENERATED_METADATA_KEYS) {
    delete customMetadata[key];
  }

  return Object.keys(customMetadata).length > 0 ? JSON.stringify(customMetadata) : '';
}

function buildHierarchyPreviewTree(nodes: ChildEntry[]): HierarchyPreviewNode[] {
  const nodeById = new Map<string, HierarchyPreviewNode>();
  for (const node of nodes) {
    nodeById.set(node.external_id, {
      ...node,
      children: [],
    });
  }

  const roots: HierarchyPreviewNode[] = [];
  nodeById.forEach((node) => {
    const parentId = node.parent_external_id;
    const parent = parentId ? nodeById.get(parentId) : null;
    if (parent && parent.external_id !== node.external_id) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items: HierarchyPreviewNode[]) => {
    items.sort((left, right) => left.name.localeCompare(right.name));
    for (const item of items) {
      sortNodes(item.children);
    }
  };

  sortNodes(roots);
  return roots;
}

function collectPreviewNodeIds(nodes: HierarchyPreviewNode[]): string[] {
  const ids: string[] = [];
  const walk = (node: HierarchyPreviewNode) => {
    ids.push(node.external_id);
    for (const child of node.children) {
      walk(child);
    }
  };

  for (const node of nodes) {
    walk(node);
  }

  return ids;
}

interface TenantHierarchyBranchProps {
  node: HierarchyPreviewNode;
  depth: number;
  expandedIds: Set<string>;
  onToggle: (nodeId: string) => void;
}

const TenantHierarchyBranch: React.FC<TenantHierarchyBranchProps> = ({
  node,
  depth,
  expandedIds,
  onToggle,
}) => {
  const hasChildren = node.children.length > 0;
  const isExpanded = expandedIds.has(node.external_id);

  return (
    <div>
      <div
        className={cn(
          'group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors',
          depth === 0 ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-700 hover:bg-white/80'
        )}
        style={{ marginLeft: `${depth * 18}px` }}
      >
        <button
          type="button"
          onClick={() => hasChildren && onToggle(node.external_id)}
          className={cn(
            'flex h-6 w-6 shrink-0 items-center justify-center rounded text-sm font-semibold text-gray-500 transition-colors',
            hasChildren ? 'hover:bg-gray-100 hover:text-gray-600' : 'invisible'
          )}
          aria-label={isExpanded ? 'Collapse branch' : 'Expand branch'}
        >
          {isExpanded ? '▾' : '▸'}
        </button>
        <span
          className={cn(
            'h-2 w-2 shrink-0 rounded-full',
            depth === 0 ? 'bg-blue-500' : 'bg-gray-300'
          )}
        />
        <span
          className={cn(
            'min-w-0 truncate',
            depth === 0 ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'
          )}
          title={node.name}
        >
          {node.name}
        </span>
        <span className="shrink-0 text-xs font-mono text-gray-400">#{node.external_id}</span>
      </div>

      {hasChildren && isExpanded && (
        <div className="ml-4 border-l border-gray-200/80 pl-2">
          {node.children.map((child) => (
            <TenantHierarchyBranch
              key={child.external_id}
              node={child}
              depth={depth + 1}
              expandedIds={expandedIds}
              onToggle={onToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const TenantHierarchyPreview: React.FC<{ nodes: ChildEntry[] }> = ({ nodes }) => {
  const tree = useMemo(() => buildHierarchyPreviewTree(nodes), [nodes]);
  const allNodeIds = useMemo(() => collectPreviewNodeIds(tree), [tree]);
  const rootNodeIds = useMemo(() => tree.map((node) => node.external_id), [tree]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setExpandedIds(new Set(allNodeIds));
  }, [allNodeIds]);

  if (nodes.length === 0) return null;

  const toggleNode = (nodeId: string) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  return (
    <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50/80 px-3 py-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide text-gray-500">
            Selected Hierarchy
          </div>
          <div className="mt-1 text-sm text-gray-600">
            {nodes.length} organization node{nodes.length === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setExpandedIds(new Set(allNodeIds))}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Expand all
          </button>
          <button
            type="button"
            onClick={() => setExpandedIds(new Set(rootNodeIds))}
            className="rounded-md border border-gray-200 bg-white px-2.5 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            Collapse
          </button>
        </div>
      </div>

      <div className="mt-3 max-h-[320px] overflow-y-auto pr-1">
        <div className="space-y-1">
          {tree.map((node) => (
            <TenantHierarchyBranch
              key={node.external_id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              onToggle={toggleNode}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Walk the tree and collect all selected nodes as ChildEntry objects,
 * preserving the parent-child relationship via parent_external_id.
 */
function extractChildrenFromTree(
  tree: SiteTreeNode[],
  selectedIds: Set<number>
): ChildEntry[] {
  const result: ChildEntry[] = [];

  function walk(node: SiteTreeNode, parentId: number | null) {
    if (selectedIds.has(node.id)) {
      result.push({
        external_id: String(node.id),
        name: node.name,
        parent_external_id: parentId && selectedIds.has(parentId) ? String(parentId) : null,
      });
    }
    for (const child of node.children) {
      walk(child, node.id);
    }
  }

  for (const root of tree) {
    walk(root, null);
  }

  return result;
}

function findNodeById(
  tree: SiteTreeNode[],
  nodeId: number
): SiteTreeNode | null {
  for (const node of tree) {
    if (node.id === nodeId) return node;
    const childMatch = findNodeById(node.children, nodeId);
    if (childMatch) return childMatch;
  }
  return null;
}

/**
 * Find selected "root" nodes — nodes that are selected but whose parent is NOT selected.
 * Used to auto-fill the tenant's external_id and name from the tree.
 */
function findSelectedRoots(
  tree: SiteTreeNode[],
  selectedIds: Set<number>
): SiteTreeNode[] {
  const roots: SiteTreeNode[] = [];

  function walk(node: SiteTreeNode, parentSelected: boolean) {
    const isSelected = selectedIds.has(node.id);
    if (isSelected && !parentSelected) {
      roots.push(node);
    }
    for (const child of node.children) {
      walk(child, isSelected);
    }
  }

  for (const root of tree) {
    walk(root, false);
  }

  return roots;
}

export const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [sourceSystems, setSourceSystems] = useState<SourceSystem[]>([]);
  const [accounts, setAccounts] = useState<NovaAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<TenantFormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Tenant | null>(null);

  // Tree picker state
  const [treePickerOpen, setTreePickerOpen] = useState(false);
  const [treeLoading, setTreeLoading] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [treeData, setTreeData] = useState<SiteTreeNode[]>([]);
  const [treeTotal, setTreeTotal] = useState(0);
  const [treeSelectedIds, setTreeSelectedIds] = useState<Set<number>>(new Set());
  const [treeSearch, setTreeSearch] = useState('');

  // Children state (what will be saved as tenant_locations)
  const [selectedChildren, setSelectedChildren] = useState<ChildEntry[]>([]);
  const [loadingChildren, setLoadingChildren] = useState(false);

  // Repeat alerts state for the edit form
  const [cadenceValue, setCadenceValue] = useState<RepeatCadenceValue>(emptyCadence);
  const [inheritedCadence, setInheritedCadence] = useState<InheritedCadence | null>(null);

  // Image display config state for the edit form. `inheritedImageDisplay`
  // represents the partner-level defaults used for placeholder hints.
  const [imageDisplayValue, setImageDisplayValue] = useState<ImageDisplayValue>({ overrides: {} });
  const [inheritedImageDisplay, setInheritedImageDisplay] = useState<{
    config: ResolvedImageDisplayConfig;
    partnerName?: string;
  } | null>(null);

  const selectedRoot = selectedChildren.find((entry) => entry.parent_external_id === null)
    || selectedChildren.find((entry) => entry.external_id === form.external_id)
    || null;
  const selectedDescendantCount = Math.max(
    selectedChildren.length - (selectedRoot ? 1 : 0),
    0
  );

  // ─── Data loading ───

  const loadTenants = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await apiCall('/api/config/tenants');
      if (resp.ok) {
        const data = await resp.json();
        setTenants(data.tenants || []);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  const loadSourceSystems = useCallback(async () => {
    try {
      const resp = await apiCall('/api/config/sources');
      if (resp.ok) {
        const data = await resp.json();
        setSourceSystems((data.source_systems || []).filter((s: SourceSystem) => s.enabled));
      }
    } catch {}
  }, []);

  const loadAccounts = useCallback(async () => {
    try {
      const resp = await apiCall('/api/config/accounts');
      if (resp.ok) {
        const data = await resp.json();
        setAccounts((data.accounts || []).filter((a: NovaAccount) => a.enabled));
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadTenants();
    loadSourceSystems();
    loadAccounts();
  }, [loadTenants, loadSourceSystems, loadAccounts]);

  // ─── Form handlers ───

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, source_system: sourceSystems[0]?.id || '' });
    setSelectedChildren([]);
    setCadenceValue(emptyCadence);
    setInheritedCadence(null);
    setImageDisplayValue({ overrides: {}, inheritAll: false });
    setInheritedImageDisplay(null);
    setError(null);
    setShowForm(true);
  };

  const openEdit = async (tenant: Tenant) => {
    setEditingId(tenant.id);
    setForm({
      source_system: tenant.source_system,
      external_id: tenant.external_id,
      name: tenant.display_name || tenant.name,
      metadata: formatEditableMetadata(tenant.metadata),
      nova_account_id: tenant.nova_account_id || '',
      enabled: tenant.enabled,
    });
    setError(null);
    setShowForm(true);

    // Reset repeat-alerts editor before re-populating from API
    setCadenceValue(emptyCadence);
    setInheritedCadence(null);
    setImageDisplayValue({ overrides: {}, inheritAll: false });
    setInheritedImageDisplay(null);

    // Load existing tenant_locations for this tenant
    setLoadingChildren(true);
    try {
      const resp = await apiCall(`/api/config/tenants/${tenant.id}`);
      if (resp.ok) {
        const data = await resp.json();
        const locations = data.locations || [];
        setSelectedChildren(
          locations.map((loc: any) => ({
            external_id: loc.external_id,
            name: loc.name || `Site ${loc.external_id}`,
            parent_external_id: loc.metadata?.parentExternalId || null,
          }))
        );

        setCadenceValue({
          enabled: data.repeat_alerts_enabled === null || data.repeat_alerts_enabled === undefined
            ? null
            : Boolean(data.repeat_alerts_enabled),
          cadenceSeconds: (data.repeat_cadence_seconds && typeof data.repeat_cadence_seconds === 'object')
            ? data.repeat_cadence_seconds
            : {},
        });
        if (data.owner_partner_id) {
          setInheritedCadence({
            enabled: data.owner_partner_repeat_alerts_enabled !== false,
            cadenceSeconds: (data.owner_partner_repeat_cadence_seconds && typeof data.owner_partner_repeat_cadence_seconds === 'object')
              ? data.owner_partner_repeat_cadence_seconds
              : {},
            partnerName: data.owner_partner_name,
          });
          // Merge partner overrides on top of system defaults so the editor can
          // show effective placeholders without a second request. System
          // defaults are mirrored in ImageDisplayEditor via the defaults API,
          // so here we just forward what the partner actually has set.
          const partnerOverrides = (data.owner_partner_image_display_config
            && typeof data.owner_partner_image_display_config === 'object')
            ? data.owner_partner_image_display_config
            : {};
          setInheritedImageDisplay({
            config: {
              show_wetness_percent: partnerOverrides.show_wetness_percent ?? false,
              show_battery: partnerOverrides.show_battery ?? true,
              show_diaper_age_ring: partnerOverrides.show_diaper_age_ring ?? true,
              show_diaper_age_text: partnerOverrides.show_diaper_age_text ?? true,
              show_diaper_model: partnerOverrides.show_diaper_model ?? true,
              show_device_name: partnerOverrides.show_device_name ?? true,
              show_signal_strength: partnerOverrides.show_signal_strength ?? true,
              language_override: partnerOverrides.language_override ?? null,
            },
            partnerName: data.owner_partner_name,
          });
        }
        setImageDisplayValue(
          deserializeImageDisplay(data.image_display_config, { tenantMode: true })
        );
      }
    } catch {} finally {
      setLoadingChildren(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    let metadata: any = null;
    if (form.metadata.trim()) {
      try {
        metadata = JSON.parse(form.metadata);
      } catch {
        setSaving(false);
        setError('Metadata must be valid JSON');
        return;
      }
    }

    try {
      const isEdit = editingId !== null;
      const url = isEdit ? `/api/config/tenants/${editingId}` : '/api/config/tenants';
      const method = isEdit ? 'PUT' : 'POST';
      const body: any = {
        source_system: form.source_system,
        external_id: form.external_id,
        name: form.name,
        metadata,
        nova_account_id: form.nova_account_id || null,
        enabled: form.enabled,
        children: selectedChildren,
        // null means "inherit from owning partner" — see RepeatCadenceEditor.
        repeat_alerts_enabled: cadenceValue.enabled,
        repeat_cadence_seconds: cadenceValue.cadenceSeconds,
        // null = fully inherit image display from owner partner; otherwise
        // a per-key override map (missing keys inherit per-key).
        image_display_config: serializeImageDisplayForTenant(imageDisplayValue),
      };

      const resp = await apiCall(url, {
        method,
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const data = await resp.json();
        throw new Error(data.error || 'Failed to save tenant');
      }
      setShowForm(false);
      await loadTenants();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const resp = await apiCall(`/api/config/tenants/${deleteTarget.id}`, { method: 'DELETE' });
    setDeleteTarget(null);
    if (!resp.ok) {
      const data = await resp.json();
      setError(data.error || 'Failed to delete tenant');
      return;
    }
    await loadTenants();
  };

  // ─── Tree picker handlers ───

  const loadSiteTree = useCallback(async (accountId: string) => {
    setTreeLoading(true);
    setTreeError(null);
    try {
      const resp = await apiCall(`/api/config/accounts/${encodeURIComponent(accountId)}/site-tree`);
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Failed to fetch site tree (HTTP ${resp.status})`);
      }
      const data = await resp.json();
      setTreeData(data.tree || []);
      setTreeTotal(data.totalSites || 0);
    } catch (err: any) {
      setTreeError(err.message || 'Failed to load site tree');
      setTreeData([]);
      setTreeTotal(0);
    } finally {
      setTreeLoading(false);
    }
  }, []);

  const openTreePicker = useCallback(async () => {
    if (!form.nova_account_id) {
      setError('Select a Nova Account first to load the organization tree.');
      return;
    }
    setTreePickerOpen(true);
    // Pre-select existing children
    const existingIds = new Set<number>();
    const currentRootId = parseInt(form.external_id, 10);
    if (!Number.isNaN(currentRootId)) {
      existingIds.add(currentRootId);
    }
    selectedChildren
      .map((c) => parseInt(c.external_id, 10))
      .filter((n) => !Number.isNaN(n))
      .forEach((id) => existingIds.add(id));
    setTreeSelectedIds(existingIds);
    await loadSiteTree(form.nova_account_id);
  }, [form.external_id, form.nova_account_id, selectedChildren, loadSiteTree]);

  const closeTreePicker = useCallback(() => {
    setTreePickerOpen(false);
    setTreeError(null);
    setTreeSearch('');
  }, []);

  const applyTreeSelection = () => {
    const roots = findSelectedRoots(treeData, treeSelectedIds);
    const selectedRootId = roots[0]?.id
      ?? Array.from(treeSelectedIds)[0]
      ?? null;

    if (selectedRootId == null) {
      setError('Select a tenant root from the organization tree before applying.');
      return;
    }

    const rootNode = findNodeById(treeData, selectedRootId);
    if (!rootNode) {
      setError('The selected tenant root could not be resolved from the organization tree.');
      return;
    }

    const nextSelectedIds = new Set<number>(
      extractChildrenFromTree([rootNode], new Set([rootNode.id, ...rootNode.descendantIds]))
        .map((entry) => parseInt(entry.external_id, 10))
        .filter((id) => !Number.isNaN(id))
    );
    const children = extractChildrenFromTree(treeData, nextSelectedIds);
    setTreeSelectedIds(nextSelectedIds);
    setSelectedChildren(children);
    setForm((f) => ({
      ...f,
      external_id: String(rootNode.id),
      name: rootNode.name,
    }));

    closeTreePicker();
  };

  // ─── Derived data ───

  const filtered = filter ? tenants.filter((t) => t.source_system === filter) : tenants;
  const sources = Array.from(new Set(tenants.map((t) => t.source_system)));
  const filteredAccounts = accounts.filter((a) => a.source_system === form.source_system);

  // ─── Render ───

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenants</h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage tenants with organization hierarchy from Nova. Select a tenant from the tree to automatically assign all child sites.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            className="border rounded-lg px-3 py-2 text-sm text-gray-700"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          >
            <option value="">All sources</option>
            {sources.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
          <button
            onClick={loadTenants}
            className="px-3 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
          >
            Add Tenant
          </button>
        </div>
      </div>

      {/* ─── Create / Edit Form ─── */}
      {showForm && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
          <h2 className="text-lg font-semibold">
            {editingId !== null ? 'Edit Tenant' : 'New Tenant'}
          </h2>
          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">{error}</div>
          )}
          {sourceSystems.length === 0 ? (
            <p className="text-sm text-gray-500">
              Add a source system first so this tenant can be linked to a known event source.
            </p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Source System */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Source System</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.source_system}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, source_system: e.target.value, nova_account_id: '' }));
                      setSelectedChildren([]);
                    }}
                  >
                    {sourceSystems.map((s) => (
                      <option key={s.id} value={s.id}>{s.id} - {s.name}</option>
                    ))}
                  </select>
                </div>

                {/* Nova Account */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Nova Account</label>
                  <select
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.nova_account_id}
                    onChange={(e) => {
                      setForm((f) => ({
                        ...f,
                        nova_account_id: e.target.value,
                        external_id: '',
                        name: '',
                      }));
                      setSelectedChildren([]);
                    }}
                  >
                    <option value="">-- None --</option>
                    {filteredAccounts.map((a) => (
                      <option key={a.id} value={a.id}>{accountDisplayName(a)}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-gray-400">
                    Link this tenant to a Nova account. Required for tree-based tenant selection.
                  </p>
                </div>

                {/* ─── Organization Tree Selection ─── */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Organization Hierarchy (Tenant Selection)
                  </label>
                  <div className="flex items-start gap-3">
                    <div className="flex-1 border rounded-lg px-3 py-2 text-sm bg-gray-50 min-h-[38px]">
                      {loadingChildren ? (
                        <span className="text-gray-400">Loading existing children…</span>
                      ) : selectedChildren.length === 0 ? (
                        <span className="text-gray-400">
                          {form.nova_account_id
                            ? 'No tenant selected. Use the organization tree to pick a tenant root and auto-assign its descendants.'
                            : 'Select a Nova Account first to access the organization tree.'}
                        </span>
                      ) : (
                        <div className="text-gray-700">
                          <div>
                            <span className="font-medium">{selectedRoot?.name || form.name || form.external_id}</span>
                            <span className="font-mono text-xs text-gray-500 ml-2">
                              #{selectedRoot?.external_id || form.external_id}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {selectedChildren.length} hierarchy node{selectedChildren.length === 1 ? '' : 's'} selected
                            {selectedDescendantCount > 0 ? ` (${selectedDescendantCount} descendant${selectedDescendantCount === 1 ? '' : 's'})` : ''}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={openTreePicker}
                      disabled={!form.nova_account_id}
                      className="px-3 py-2 border rounded-lg text-sm font-medium text-blue-600 border-blue-200 bg-blue-50 hover:bg-blue-100 disabled:opacity-50 whitespace-nowrap"
                    >
                      Select from Tree
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">
                    Pick a tenant root from the Nova organization hierarchy. All descendant
                    tenants and sites will be assigned automatically and stored in
                    <code className="mx-1 rounded bg-gray-100 px-1 py-0.5 font-mono">tenant_locations</code>.
                  </p>
                </div>

                <TenantHierarchyPreview nodes={selectedChildren} />

                {/* External ID */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">External ID</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.external_id}
                    onChange={(e) => setForm((f) => ({ ...f, external_id: e.target.value }))}
                    placeholder="Auto-filled from tree or enter manually"
                  />
                </div>

                {/* Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    value={form.name}
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Auto-filled from tree or enter manually"
                  />
                </div>

                {/* Enabled */}
                <label className="flex items-center gap-2 text-sm text-gray-700 mt-6">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  />
                  Enabled
                </label>

                {/* Repeat alerts */}
                <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Repeat Alerts (override)</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional per-tenant override of repeat-alert cadence. Leave fields blank to inherit
                      {inheritedCadence
                        ? ` from ${inheritedCadence.partnerName ? `partner "${inheritedCadence.partnerName}"` : 'the owning partner'}.`
                        : ' from the system default (this tenant has no owning partner yet).'}
                    </p>
                  </div>
                  <RepeatCadenceEditor
                    value={cadenceValue}
                    onChange={setCadenceValue}
                    partnerDefaults={inheritedCadence
                      ? { enabled: inheritedCadence.enabled, cadenceSeconds: inheritedCadence.cadenceSeconds }
                      : undefined}
                    parentLabel={inheritedCadence?.partnerName ? `partner "${inheritedCadence.partnerName}"` : undefined}
                  />
                </div>

                {/* Image display config (tenant override) */}
                <div className="md:col-span-2 rounded-lg border border-gray-200 bg-gray-50/60 p-4">
                  <div className="mb-3">
                    <h3 className="text-sm font-semibold text-gray-800">Image Display (override)</h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Optional per-tenant override for image toggles.
                      {inheritedImageDisplay
                        ? ` Unchecked rows inherit from ${inheritedImageDisplay.partnerName ? `partner "${inheritedImageDisplay.partnerName}"` : 'the owning partner'} (then system defaults).`
                        : ' This tenant has no owning partner yet, so unchecked rows inherit from the system defaults.'}
                    </p>
                  </div>
                  <ImageDisplayEditor
                    value={imageDisplayValue}
                    onChange={setImageDisplayValue}
                    partnerDefaults={inheritedImageDisplay?.config}
                    parentLabel={inheritedImageDisplay?.partnerName ? `partner "${inheritedImageDisplay.partnerName}"` : undefined}
                    allowInheritAll={!!inheritedImageDisplay}
                  />
                </div>

                {/* Metadata JSON */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Extra Metadata JSON</label>
                  <input
                    className="w-full border rounded-lg px-3 py-2 text-sm font-mono"
                    value={form.metadata}
                    onChange={(e) => setForm((f) => ({ ...f, metadata: e.target.value }))}
                    placeholder='{"groups":[]}'
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Tenant hierarchy metadata is rebuilt from the selected tree and shown above.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ─── Tenant Table ─── */}
      {loading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-sm text-gray-400">
          Loading...
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Name</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Source</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">External ID</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Account</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Hierarchy</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Partners</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Last Seen</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Status</th>
                <th className="text-right px-5 py-3 font-semibold text-gray-600">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => (
                <tr key={t.id} className="border-b border-gray-100 last:border-0">
                  <td className="px-5 py-3 font-medium text-gray-900">
                    {t.display_name || t.name}
                  </td>
                  <td className="px-5 py-3">
                    <span className="text-xs font-mono bg-gray-100 px-2 py-0.5 rounded">
                      {t.source_system}
                    </span>
                  </td>
                  <td className="px-5 py-3 font-mono text-gray-600">{t.external_id}</td>
                  <td className="px-5 py-3 text-gray-500 text-xs font-mono">
                    {t.account_label || t.nova_account_id || '--'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={cn(
                        'text-xs font-medium px-2 py-0.5 rounded',
                        parseInt(t.location_count || '0') > 0
                          ? 'bg-indigo-50 text-indigo-700'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      {t.location_count || '0'} node(s)
                    </span>
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={cn(
                        'text-xs font-medium px-2 py-0.5 rounded',
                        parseInt(t.partner_count) > 0
                          ? 'bg-blue-50 text-blue-700'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      {t.partner_count} partner(s)
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500 text-xs">
                    {t.last_seen ? getTimeAgo(t.last_seen) : 'Never'}
                  </td>
                  <td className="px-5 py-3">
                    <span
                      className={cn(
                        'text-xs font-medium px-2 py-0.5 rounded',
                        t.enabled
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-gray-100 text-gray-500'
                      )}
                    >
                      {t.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right space-x-2">
                    <button
                      onClick={() => openEdit(t)}
                      className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setDeleteTarget(t)}
                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-5 py-8 text-center text-gray-400">
                    No tenants found. Add one to get started, or they are auto-discovered when the
                    poller runs.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-xs text-gray-400">
        Total: {filtered.length} tenant(s)
        {filter && ` (filtered from ${tenants.length})`}
      </div>

      {/* ─── Tree Picker Modal ─── */}
      {treePickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  Select Tenant from Organization Tree
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Organization tree for account{' '}
                  <span className="font-mono">
                    {(() => {
                      const acct = accounts.find((a) => a.id === form.nova_account_id);
                      return acct ? accountDisplayName(acct) : form.nova_account_id;
                    })()}
                  </span>
                  . Selecting a tenant root automatically assigns all of its descendants.
                </p>
              </div>
              <button
                type="button"
                onClick={closeTreePicker}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>

            {/* Search */}
            <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
              <input
                className="flex-1 border rounded-lg px-3 py-2 text-sm"
                placeholder="Search by tenant name or site ID…"
                value={treeSearch}
                onChange={(e) => setTreeSearch(e.target.value)}
              />
              <button
                type="button"
                onClick={() => form.nova_account_id && loadSiteTree(form.nova_account_id)}
                disabled={treeLoading || !form.nova_account_id}
                className="px-3 py-2 border rounded-lg text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap"
              >
                {treeLoading ? 'Loading…' : 'Reload'}
              </button>
            </div>

            {/* Tree body */}
            <div className="px-6 py-4 overflow-auto flex-1">
              {treeError && (
                <div className="mb-3 text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                  {treeError}
                </div>
              )}
              {treeLoading ? (
                <div className="py-10 text-center text-sm text-gray-400">
                  Logging into Nova and fetching the organization tree…
                </div>
              ) : (
                <>
                  <div className="mb-2 text-xs text-gray-500">
                    Fetched {treeTotal} site{treeTotal === 1 ? '' : 's'} from Nova
                    /_ah/api/admin/v3/site
                  </div>
                  <TenantTree
                    tree={treeData}
                    selectedIds={treeSelectedIds}
                    onChange={setTreeSelectedIds}
                    search={treeSearch}
                  />
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-xs text-gray-500">
                {treeSelectedIds.size} organization node{treeSelectedIds.size === 1 ? '' : 's'} will
                be assigned to this tenant hierarchy.
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={closeTreePicker}
                  className="px-4 py-2 border rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={applyTreeSelection}
                  disabled={treeLoading}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  Apply selection
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Confirmation ─── */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Are you sure you want to delete this?"
        message={`This will permanently delete the tenant "${deleteTarget?.display_name || deleteTarget?.name}".`}
        warning="This tenant will not be auto-regenerated by the poller. Associated locations and partner assignments will also be deleted."
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};
